import {Opening} from "../../../../api/Openings";
import {Divider, ListItemText, MenuItem, Typography} from "@mui/material";
import {useContext, useState} from "react";
import {useSnackbar} from "../../../../misc/Snackbar";
import { api } from "../../../../api/API";
import {useProjectId} from "../ProjectName";
import {TableContext} from "../../../../misc/scroller/Table";
import {css} from "@emotion/css";
import {orange} from "@mui/material/colors";
import {ChangeHistory} from "./ChangeHistory";
import {Link} from "react-router-dom";
import {logisticsTableName} from "../logistics/Logistics";
import {buildFilterLink} from "../../../../misc/scroller/Filter";
import {RowActionsBase} from "../../quote/openings/RowActions";
import {CheckboxContent} from "../Checkbox";
import {useSnackAction} from "../hardware/SnackAction";
import {useConfirm} from "../../../../misc/Confirm";

export function RowActions(props: {
    row: Opening
}) {
    const [showChangeHistory, setShowChangeHistory] = useState(false);

    const project = useProjectId();
    const snack = useSnackbar();
    const tbl = useContext(TableContext);
    const confirm = useConfirm();

    const resetConflict = useSnackAction(async (input: {project: number; opening: number}) => {
        await api.openings.resetConflict(input);
        await tbl.reload(true);
    }, {
        success: props.row.backup ? "Reset row to its original value" : "Recalculated and accepted conflict",
    });

    // pre-rendered elevation images, populated by the elevation tool's render-on-save.
    // absent id => no rendering available, so the menu item is greyed out.
    const viewRendering = (fileId: number | undefined, onClose: () => void) => {
        if(!fileId) return;
        window.open(api.files.downloadLink(fileId, "inline"), "_blank");
        onClose();
    };

    const recalculate = useSnackAction(async (input: {project: number; openings: number[]}) => {
        await api.openings.recalculate(input);
        await tbl.reload(true);
    }, {
        success: "Recalculated",
    });

    return (<>
        <RowActionsBase draggable moreIcons={<div style={{marginTop: 5, marginLeft: 5}}><CheckboxContent id={props.row.id} /></div>}>
            {onClose => <>
                <Link to={`/project/${project}/shop-drawing/logistics?${buildFilterLink({
                    tableName:logisticsTableName(project, "openings"),
                    filter: {
                        type: "eq",
                        search: {
                            opening: props.row.id
                        },
                    }
                })}`} style={{textDecoration: "none", color: "inherit"}}>
                    <MenuItem onClick={() => {
                        onClose();
                    }}>
                        <ListItemText>View in Logistics</ListItemText>
                    </MenuItem>
                </Link>
                <Divider />
                <MenuItem onClick={() => {
                    setShowChangeHistory(true)
                    onClose();
                }}>
                    <ListItemText>Change History</ListItemText>
                    {props.row.historyCount > 0 && <Typography variant="body2" color="text.secondary" className={changeHistoryCircleNumber}>
                        {props.row.historyCount}
                    </Typography>}
                </MenuItem>
                <MenuItem onClick={async () => {
                    snack.loading();
                    try {
                        await api.openings.upsert(Object.assign({}, props.row, {
                            id: 0,
                            name: props.row.name + " (duplicate)",
                            sortOrder: props.row.sortOrder+1,
                        }));

                        snack.success("Duplicated")
                        await tbl.reload(true);
                    } catch (e: any) {
                        snack.error(e.toString());
                    }
                    onClose();
                }}>
                    <ListItemText>Duplicate Row</ListItemText>
                </MenuItem>
                <MenuItem disabled={recalculate.loading} onClick={() => {
                    recalculate.callback({project: project, openings: [props.row.id]});
                    onClose();
                }}>
                    <ListItemText>Recalculate</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem disabled={!props.row.drwScreenRendering}
                          onClick={() => viewRendering(props.row.drwScreenRendering, onClose)}>
                    <ListItemText>View Screen Elevation</ListItemText>
                </MenuItem>
                <MenuItem disabled={!props.row.drwDoorRendering}
                          onClick={() => viewRendering(props.row.drwDoorRendering, onClose)}>
                    <ListItemText>View Door Elevation</ListItemText>
                </MenuItem>
                <MenuItem disabled={!props.row.drwHardwareRendering}
                          onClick={() => viewRendering(props.row.drwHardwareRendering, onClose)}>
                    <ListItemText>View Hardware Elevation</ListItemText>
                </MenuItem>
                {props.row.logictrl?.conflictsWithMain && <>
                    <Divider />
                    <MenuItem disabled={resetConflict.loading} onClick={async () => {
                        const message = props.row.backup
                            ? `Reset "${props.row.name}" (and related wall-types/screen elevations...) to their original values? This discards all changes to this row in the current changeset.`
                            : `"${props.row.name}" was not edited in this change. Recalculate it and accept the conflict?`;

                        const ok = await confirm.confirm(message).catch(() => false);
                        if(!ok) return;

                        resetConflict.callback({project: project, opening: props.row.id});
                        onClose();
                    }}>
                        <ListItemText>Reset Conflict</ListItemText>
                    </MenuItem>
                </>}
            </>}
        </RowActionsBase>

        {showChangeHistory && <ChangeHistory opening={props.row} onDone={() => setShowChangeHistory(false)} />}
    </>);
}

export const changeHistoryCircleNumber = css({
    color: orange["700"] + " !important",
    border: "1px solid " + orange["700"],
    textAlign: "center",
    width: "1.5em",
    height: "1.5em",
    borderRadius: "50%",
    marginLeft: "1em !important",
} as any)